import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { hasAdminAccess } from '../utils/permissionConfig';
import { buildAuthHeaders, ensureCsrfToken } from '../utils/auth';

function RegisterPage() {
  const { userInfo, isUserLoading } = useContext(UserContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_login_id: '',
    user_name: '',
    password: '',
    email: '',
    status: '',
    location: '',
    level: 'client',
    preferred_language: 'ko'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // admin 권한 체크 (개발 환경에서만 디버깅)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('RegisterPage - userInfo:', userInfo ? '[PRESENT]' : '[MISSING]');
      console.log('RegisterPage - isUserLoading:', isUserLoading);
    }

    if (isUserLoading) {
      // 사용자 정보가 로딩 중인 경우 대기
      console.log('사용자 정보 로딩 중...');
      return;
    }

    if (!userInfo) {
      console.log('사용자 정보가 없습니다.');
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    // 사용자 정보의 모든 필드 확인 (개발 환경에서만)
    if (process.env.NODE_ENV === 'development') {
      console.log('사용자 정보:', userInfo ? '[PRESENT]' : '[MISSING]');
      console.log('사용자 레벨 필드들:', {
        user_level: userInfo.user_level,
        level: userInfo.level,
        userLevel: userInfo.userLevel
      });
    }

    // level 또는 user_level 중 존재하는 필드 사용
    const userLevel = userInfo.level || userInfo.user_level || userInfo.userLevel;
    console.log('확인된 사용자 레벨:', userLevel);

    if (!hasAdminAccess(userInfo)) {
      console.log('관리자 권한이 없습니다.');
      alert('관리자만 접근할 수 있습니다.');
      navigate('/');
      return;
    }

    console.log('관리자 권한 확인됨');
  }, [userInfo, isUserLoading, navigate]);

  // 로딩 중이거나 사용자 정보가 없는 경우
  if (isUserLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        사용자 정보를 불러오는 중...
      </div>
    );
  }

  // 사용자 정보가 없는 경우
  if (!userInfo) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        로그인이 필요합니다.
      </div>
    );
  }

  // admin 또는 특수 사용자가 아닌 경우
  if (!hasAdminAccess(userInfo)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        관리자 권한이 필요합니다.
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await ensureCsrfToken({ forceRefresh: true });

      // 유효성 검사
      if (!formData.user_login_id.trim()) {
        setError('아이디를 입력해주세요.');
        return;
      }
      if (!formData.user_name.trim()) {
        setError('사용자 이름을 입력해주세요.');
        return;
      }
      if (!formData.password.trim()) {
        setError('비밀번호를 입력해주세요.');
        return;
      }
      if (formData.password.length < 6) {
        setError('비밀번호는 최소 6자 이상이어야 합니다.');
        return;
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('올바른 이메일 형식을 입력해주세요.');
        return;
      }

      const response = await fetch('/api/register', {
        method: 'POST',
        headers: buildAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        alert('회원가입 성공!');
        navigate('/admin');
      } else {
        setError(data.message || '회원가입에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결 오류가 발생했습니다.');
      console.error('회원가입 오류:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      padding: '20px',
      paddingBottom: '100px',
      fontFamily: 'Arial, sans-serif',
      height: '100%',
      overflow: 'auto',
      boxSizing: 'border-box'
    }}>
      <h2 style={{
        textAlign: 'center',
        marginBottom: '30px',
        color: '#333',
        fontSize: '24px'
      }}>
        유저 등록
      </h2>

      <form onSubmit={handleSubmit} style={{
        background: '#f9f9f9',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        {/* 필수 필드 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px', color: '#555', fontSize: '16px' }}>기본 정보</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              아이디 *
            </label>
            <input
              name="user_login_id"
              type="text"
              placeholder="로그인 아이디 (영문, 숫자 조합)"
              onChange={handleChange}
              value={formData.user_login_id}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              사용자 이름 *
            </label>
            <input
              name="user_name"
              type="text"
              placeholder="실명 또는 닉네임"
              onChange={handleChange}
              value={formData.user_name}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              비밀번호 *
            </label>
            <input
              name="password"
              type="password"
              placeholder="최소 6자 이상"
              onChange={handleChange}
              value={formData.password}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        {/* 선택 필드 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px', color: '#555', fontSize: '16px' }}>추가 정보</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              이메일
            </label>
            <input
              name="email"
              type="email"
              placeholder="이메일 주소 (선택사항)"
              onChange={handleChange}
              value={formData.email}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              상태
            </label>
            <input
              name="status"
              type="text"
              placeholder="예: 영업중, 휴무중, 방문중"
              onChange={handleChange}
              value={formData.status}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              위치
            </label>
            <input
              name="location"
              type="text"
              placeholder="예: Paris, Seoul, Tokyo"
              onChange={handleChange}
              value={formData.location}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        {/* 사용자 레벨 및 언어 설정 */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px', color: '#555', fontSize: '16px' }}>권한 설정</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              사용자 레벨 *
            </label>
            <select
              name="level"
              onChange={handleChange}
              value={formData.level}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="client">클라이언트</option>
              <option value="guest_artist">게스트 아티스트</option>
              <option value="staff_artist">스태프 아티스트</option>
              <option value="admin">관리자</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              선호 언어
            </label>
            <select
              name="preferred_language"
              onChange={handleChange}
              value={formData.preferred_language}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="ko">한국어</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            color: '#dc3545',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* 버튼 */}
        <div style={{ textAlign: 'center' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginRight: '10px'
            }}
          >
            {loading ? '등록 중...' : '유저 등록'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            취소
          </button>
        </div>
      </form>
    </div>
  );
}

export default RegisterPage;
